import { Router } from "express";
import { db } from "@workspace/db";
import {
  tendersTable, usersTable, vendorsTable, analysesTable, companiesTable, subscriptionsTable,
} from "@workspace/db";
import { eq, sql, count, desc } from "drizzle-orm";
import { requireAdmin, type AuthRequest } from "../lib/auth";

const router = Router();

/* ─── Bootstrap settings table ──────────────────────────────── */
async function ensureSettingsTable() {
  await db.execute(sql`
    CREATE TABLE IF NOT EXISTS platform_settings (
      key   TEXT PRIMARY KEY,
      value TEXT NOT NULL
    )
  `);
}
ensureSettingsTable().catch(() => {});

async function getSetting(key: string): Promise<string | null> {
  const rows = await db.execute(sql`SELECT value FROM platform_settings WHERE key = ${key}`);
  return (rows.rows?.[0] as any)?.value ?? null;
}

async function getAllSettings(): Promise<Record<string, string>> {
  const rows = await db.execute(sql`SELECT key, value FROM platform_settings`);
  const result: Record<string, string> = {};
  for (const row of (rows.rows ?? []) as any[]) result[row.key] = row.value;
  return result;
}

async function setSetting(key: string, value: string) {
  await db.execute(sql`
    INSERT INTO platform_settings (key, value) VALUES (${key}, ${value})
    ON CONFLICT (key) DO UPDATE SET value = EXCLUDED.value
  `);
}

/* ─── Export getSetting for use in openai.ts ─────────────────── */
export { getSetting };

const MASKED = "••••••••";
function mask(v: string | undefined | null): string {
  if (!v) return "";
  return MASKED;
}
function lastFour(v: string | undefined | null): string {
  if (!v) return "";
  return `...${v.slice(-4)}`;
}

/* ─── GET /admin/settings ───────────────────────────────────── */
router.get("/admin/settings", requireAdmin, async (_req: AuthRequest, res, next) => {
  try {
    const s = await getAllSettings();
    const envStripeSecret = process.env.STRIPE_SECRET_KEY;
    const envStripePublishable = process.env.STRIPE_PUBLISHABLE_KEY;
    const envStripeWebhook = process.env.STRIPE_WEBHOOK_SECRET;
    const envOpenAI = process.env.OPENAI_API_KEY;

    res.json({
      /* AI */
      aiModel: s["ai_model"] ?? "gpt-4o",
      hasCustomOpenAiKey: !!(s["ai_api_key"] || envOpenAI),
      openAiKeyHint: s["ai_api_key"] ? lastFour(s["ai_api_key"]) : (envOpenAI ? lastFour(envOpenAI) : ""),
      openAiKeySource: s["ai_api_key"] ? "db" : (envOpenAI ? "env" : "none"),
      aiApiKey: "",
      /* Stripe */
      hasStripeSecretKey: !!(s["stripe_secret_key"] || envStripeSecret),
      stripeSecretKeyHint: s["stripe_secret_key"] ? lastFour(s["stripe_secret_key"]) : (envStripeSecret ? lastFour(envStripeSecret) : ""),
      stripeSecretKeySource: s["stripe_secret_key"] ? "db" : (envStripeSecret ? "env" : "none"),
      stripeSecretKey: "",
      hasStripePublishableKey: !!(s["stripe_publishable_key"] || envStripePublishable),
      stripePublishableKeyHint: s["stripe_publishable_key"] ? lastFour(s["stripe_publishable_key"]) : (envStripePublishable ? lastFour(envStripePublishable) : ""),
      stripePublishableKeySource: s["stripe_publishable_key"] ? "db" : (envStripePublishable ? "env" : "none"),
      stripePublishableKey: "",
      hasStripeWebhookSecret: !!(s["stripe_webhook_secret"] || envStripeWebhook),
      stripeWebhookSecretHint: s["stripe_webhook_secret"] ? lastFour(s["stripe_webhook_secret"]) : (envStripeWebhook ? lastFour(envStripeWebhook) : ""),
      stripeWebhookSecretSource: s["stripe_webhook_secret"] ? "db" : (envStripeWebhook ? "env" : "none"),
      stripeWebhookSecret: "",
      /* Prompts */
      systemPromptAr: s["system_prompt_ar"] ?? "",
      systemPromptEn: s["system_prompt_en"] ?? "",
      proposalPromptAr: s["proposal_prompt_ar"] ?? "",
      proposalPromptEn: s["proposal_prompt_en"] ?? "",
      /* Landing */
      landingTitleAr: s["landing_title_ar"] ?? "المساعد الذكي للمناقصات الحكومية السعودية",
      landingTitleEn: s["landing_title_en"] ?? "AI Tender Copilot Saudi",
      landingSubtitleAr: s["landing_subtitle_ar"] ?? "حلل المناقصات الحكومية السعودية بالذكاء الاصطناعي",
      landingSubtitleEn: s["landing_subtitle_en"] ?? "Analyze Saudi government tenders with AI precision",
      /* VAT */
      vatEnabled: s["vat_enabled"] === "false" ? false : true, // Default to true
    });
  } catch (err) { next(err); }
});

/* ─── PUT /admin/settings ───────────────────────────────────── */
router.put("/admin/settings", requireAdmin, async (req: AuthRequest, res, next) => {
  try {
    const {
      aiModel, aiApiKey,
      stripeSecretKey, stripePublishableKey, stripeWebhookSecret,
      systemPromptAr, systemPromptEn,
      proposalPromptAr, proposalPromptEn,
      landingTitleAr, landingTitleEn, landingSubtitleAr, landingSubtitleEn,
      vatEnabled,
    } = req.body;

    const updates: [string, string][] = [];
    if (aiModel) updates.push(["ai_model", aiModel]);
    if (aiApiKey && aiApiKey !== MASKED) updates.push(["ai_api_key", aiApiKey]);
    if (stripeSecretKey && stripeSecretKey !== MASKED) updates.push(["stripe_secret_key", stripeSecretKey]);
    if (stripePublishableKey && stripePublishableKey !== MASKED) updates.push(["stripe_publishable_key", stripePublishableKey]);
    if (stripeWebhookSecret && stripeWebhookSecret !== MASKED) updates.push(["stripe_webhook_secret", stripeWebhookSecret]);
    if (systemPromptAr !== undefined) updates.push(["system_prompt_ar", systemPromptAr]);
    if (systemPromptEn !== undefined) updates.push(["system_prompt_en", systemPromptEn]);
    if (proposalPromptAr !== undefined) updates.push(["proposal_prompt_ar", proposalPromptAr]);
    if (proposalPromptEn !== undefined) updates.push(["proposal_prompt_en", proposalPromptEn]);
    if (landingTitleAr !== undefined) updates.push(["landing_title_ar", landingTitleAr]);
    if (landingTitleEn !== undefined) updates.push(["landing_title_en", landingTitleEn]);
    if (landingSubtitleAr !== undefined) updates.push(["landing_subtitle_ar", landingSubtitleAr]);
    if (landingSubtitleEn !== undefined) updates.push(["landing_subtitle_en", landingSubtitleEn]);
    if (vatEnabled !== undefined) updates.push(["vat_enabled", vatEnabled ? "true" : "false"]);

    await Promise.all(updates.map(([k, v]) => setSetting(k, v)));
    res.json({ success: true });
  } catch (err) { next(err); }
});

/* ─── GET /admin/reports ─────────────────────────────────────── */
router.get("/admin/reports", requireAdmin, async (_req: AuthRequest, res, next) => {
  try {
    const [
      totalTenders, totalUsers, totalVendors, totalAnalyses,
      totalCompanies, activeSubscriptions,
      tendersByStatus, tendersByMonth, usersByMonth,
    ] = await Promise.all([
      db.select({ c: count() }).from(tendersTable),
      db.select({ c: count() }).from(usersTable),
      db.select({ c: count() }).from(vendorsTable),
      db.select({ c: count() }).from(analysesTable),
      db.select({ c: count() }).from(companiesTable),
      db.select({ c: count() }).from(subscriptionsTable).where(eq(subscriptionsTable.status, "active")),
      db.select({ status: tendersTable.status, c: count() })
        .from(tendersTable).groupBy(tendersTable.status),
      db.execute(sql`
        SELECT to_char(date_trunc('month', created_at), 'YYYY-MM') AS month, count(*)::int AS count
        FROM tenders
        WHERE created_at >= now() - interval '6 months'
        GROUP BY month ORDER BY month
      `),
      db.execute(sql`
        SELECT to_char(date_trunc('month', created_at), 'YYYY-MM') AS month, count(*)::int AS count
        FROM users
        WHERE created_at >= now() - interval '6 months'
        GROUP BY month ORDER BY month
      `),
    ]);

    res.json({
      totals: {
        tenders: Number(totalTenders[0]?.c ?? 0),
        users: Number(totalUsers[0]?.c ?? 0),
        vendors: Number(totalVendors[0]?.c ?? 0),
        analyses: Number(totalAnalyses[0]?.c ?? 0),
        companies: Number(totalCompanies[0]?.c ?? 0),
        activeSubscriptions: Number(activeSubscriptions[0]?.c ?? 0),
      },
      tendersByStatus: tendersByStatus.map((r) => ({ status: r.status, count: Number(r.c) })),
      tendersByMonth: tendersByMonth.rows ?? [],
      usersByMonth: usersByMonth.rows ?? [],
    });
  } catch (err) { next(err); }
});

/* ─── GET /admin/vendors ─────────────────────────────────────── */
router.get("/admin/vendors", requireAdmin, async (_req: AuthRequest, res, next) => {
  try {
    const vendors = await db
      .select()
      .from(vendorsTable)
      .orderBy(vendorsTable.isVerified, desc(vendorsTable.createdAt));
    res.json(vendors);
  } catch (err) { next(err); }
});

/* ─── PATCH /admin/vendors/:id ───────────────────────────────── */
router.patch("/admin/vendors/:id", requireAdmin, async (req: AuthRequest, res, next) => {
  try {
    const id = Number(req.params["id"]);
    const { isVerified, isActive } = req.body;
    const updated = await db
      .update(vendorsTable)
      .set({
        ...(isVerified !== undefined && { isVerified }),
        ...(isActive !== undefined && { isActive }),
        updatedAt: new Date(),
      })
      .where(eq(vendorsTable.id, id))
      .returning();
    if (!updated[0]) return res.status(404).json({ error: "Vendor not found" });
    res.json(updated[0]);
  } catch (err) { next(err); }
});

export default router;
export { getSetting as getAdminSetting };
